import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useAuth } from '@/app/providers/authContext';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';

import type {
  CancelOrderRequest,
  HistoryFilters,
  ItemHistory,
  Order,
  OrderListFilters,
  OrderPage,
  PeriodHistory,
  ReorderResult,
} from './contracts';

const ORDERS = '/v1/orders';

/** Page size chosen the way the catalogue's is: the first screenful in one request. */
const PAGE_SIZE = 20;

interface TenantBranch {
  readonly key: string;
  readonly enabled: boolean;
}

function useTenantBranch(): TenantBranch {
  const { tenantId } = useAuth();
  return { key: tenantId ?? 'sem-tenant', enabled: tenantId !== null };
}

function toQueryParams(filters: OrderListFilters, cursor: string | undefined) {
  return {
    limit: PAGE_SIZE,
    ...(filters.from === undefined ? {} : { from: filters.from }),
    ...(filters.to === undefined ? {} : { to: filters.to }),
    ...(filters.establishmentId === undefined ? {} : { establishment_id: filters.establishmentId }),
    ...(filters.status === undefined ? {} : { status: filters.status }),
    ...(cursor === undefined ? {} : { cursor }),
  };
}

/**
 * The order history list — cursor-paged, same envelope and the same `has_more`-is-the-authority
 * rule the catalogue's own listing follows (`features/catalog/queries.ts`).
 */
export function useOrdersQuery(
  filters: OrderListFilters,
  establishmentId: string | undefined,
): UseInfiniteQueryResult<{ pages: OrderPage[] }, Error> {
  const { key, enabled } = useTenantBranch();

  return useInfiniteQuery({
    queryKey: [...queryKeys.orders(key, establishmentId ?? 'sem-estabelecimento'), 'list', filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api.get<OrderPage>(ORDERS, { query: toQueryParams(filters, pageParam), signal }),
    // `has_more` is the authority, not a non-empty `next_cursor`, and `page` itself is optional
    // on the generated envelope — the same defensive read `features/catalog/queries.ts` uses.
    getNextPageParam: (lastPage) =>
      lastPage.page?.has_more === true ? (lastPage.page.next_cursor ?? undefined) : undefined,
    enabled: enabled && establishmentId !== undefined,
  });
}

export function useOrderQuery(orderId: string | undefined): UseQueryResult<Order, Error> {
  const { key, enabled } = useTenantBranch();

  return useQuery({
    queryKey: [...queryKeys.all(key), 'order', orderId ?? ''],
    queryFn: ({ signal }) =>
      api.get<Order>(`${ORDERS}/${encodeURIComponent(orderId ?? '')}`, { signal }),
    enabled: enabled && orderId !== undefined && orderId !== '',
  });
}

function useOrdersInvalidation(establishmentId: string | undefined): () => Promise<void> {
  const queryClient = useQueryClient();
  const { key } = useTenantBranch();

  return async () => {
    if (establishmentId === undefined) {
      return;
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.orders(key, establishmentId) });
  };
}

export interface CancelOrderInput {
  readonly orderId: string;
  readonly reason: string;
}

/** Only accepted from `placed` — the database's own transition table refuses every other edge. */
export function useCancelOrderMutation(
  establishmentId: string | undefined,
): UseMutationResult<Order, Error, CancelOrderInput> {
  const queryClient = useQueryClient();
  const { key } = useTenantBranch();
  const invalidateList = useOrdersInvalidation(establishmentId);

  return useMutation({
    mutationFn: ({ orderId, reason }: CancelOrderInput) =>
      api.post<Order>(`${ORDERS}/${encodeURIComponent(orderId)}/cancel`, {
        reason,
      } satisfies CancelOrderRequest),
    onSuccess: async (_order, { orderId }) => {
      await invalidateList();
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.all(key), 'order', orderId] });
    },
  });
}

/**
 * Refills the caller's open cart from a previous order. This is the whole of what "reorder" does
 * on the wire — it does not place a new order — so the screen's job after it succeeds is to send
 * the buyer to the cart it just refilled, carrying the adjustment report along as a toast.
 *
 * Invalidates the cart branch as well as the orders list: a report naming lines that were carried,
 * adjusted or skipped is only trustworthy if the cart the buyer is about to see is the one the
 * server just built, not a cached one from before the request.
 */
export function useReorderMutation(
  establishmentId: string | undefined,
): UseMutationResult<ReorderResult, Error, string> {
  const queryClient = useQueryClient();
  const { key } = useTenantBranch();

  return useMutation({
    mutationFn: (orderId: string) =>
      api.post<ReorderResult>(`${ORDERS}/${encodeURIComponent(orderId)}/reorder`),
    onSuccess: async () => {
      if (establishmentId === undefined) {
        return;
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.cart(key, establishmentId) });
    },
  });
}

/**
 * "Editar pedido", inside the window: the order's lines go back to the cart with their exact
 * quantities and the order is withdrawn, in one call — its lines are immutable evidence of what was
 * charged, so editing is "back to the cart". Placing it again opens a new window. Invalidates the
 * order, the list and the cart, which all changed.
 */
export function useEditOrderMutation(
  establishmentId: string | undefined,
): UseMutationResult<ReorderResult, Error, string> {
  const queryClient = useQueryClient();
  const { key } = useTenantBranch();
  const invalidateList = useOrdersInvalidation(establishmentId);

  return useMutation({
    mutationFn: (orderId: string) =>
      api.post<ReorderResult>(`${ORDERS}/${encodeURIComponent(orderId)}/edit`),
    onSuccess: async (_result, orderId) => {
      await invalidateList();
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.all(key), 'order', orderId] });
      if (establishmentId !== undefined) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.cart(key, establishmentId) });
      }
    },
  });
}

function toHistoryParams(filters: HistoryFilters) {
  return { from: filters.from, to: filters.to };
}

export function useItemHistoryQuery(
  filters: HistoryFilters,
  active = true,
): UseQueryResult<ItemHistory, Error> {
  const { key, enabled: tenantEnabled } = useTenantBranch();
  const enabled = tenantEnabled && active;

  return useQuery({
    queryKey: [...queryKeys.all(key), 'orders-history', 'by-item', filters],
    queryFn: ({ signal }) =>
      api.get<ItemHistory>(`${ORDERS}/history/by-item`, {
        query: toHistoryParams(filters),
        signal,
      }),
    enabled,
  });
}

export function usePeriodHistoryQuery(
  filters: HistoryFilters,
): UseQueryResult<PeriodHistory, Error> {
  const { key, enabled } = useTenantBranch();

  return useQuery({
    queryKey: [...queryKeys.all(key), 'orders-history', 'by-period', filters],
    queryFn: ({ signal }) =>
      api.get<PeriodHistory>(`${ORDERS}/history/by-period`, {
        query: toHistoryParams(filters),
        signal,
      }),
    enabled,
  });
}
